import React, { useState } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useDatabases } from '../hooks/useDatabases';
import { toast } from 'react-toastify';

export function DatabaseConnectionModal({ isOpen, onClose, onSuccess }) {
  const { create, testConnection, loading } = useDatabases();
  const [formData, setFormData] = useState({
    dbName: '',
    dbUser: '',
    dbPassword: '',
  });
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTestConnection = async () => {
    try {
      setIsTesting(true);
      const result = await testConnection({
        dbName: formData.dbName,
        dbUser: formData.dbUser,
        dbPassword: formData.dbPassword,
      });
      setTestResult(result);
      if (result.success) {
        toast.success('✅ Connection successful!');
      } else {
        toast.error(`❌ ${result.message}`);
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!testResult?.success) {
      toast.error('Please test the connection first');
      return;
    }

    try {
      await create({
        dbName: formData.dbName,
        dbUser: formData.dbUser,
        dbPassword: formData.dbPassword,
      });

      toast.success('Database configuration saved successfully!');
      setFormData({
        dbName: '',
        dbUser: '',
        dbPassword: '',
      });
      setTestResult(null);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(`Failed to save: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Add Database</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* DB Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DB_NAME *
            </label>
            <input
              type="text"
              name="dbName"
              value={formData.dbName}
              onChange={handleChange}
              placeholder="Database name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* DB User */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DB_USER *
            </label>
            <input
              type="text"
              name="dbUser"
              value={formData.dbUser}
              onChange={handleChange}
              placeholder="Database username"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* DB Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DB_PASSWORD *
            </label>
            <input
              type="password"
              name="dbPassword"
              value={formData.dbPassword}
              onChange={handleChange}
              placeholder="Database password"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              className={`p-3 rounded-md flex items-start gap-2 text-sm ${
                testResult.success
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              {testResult.success ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <p
                className={
                  testResult.success ? 'text-green-800' : 'text-red-800'
                }
              >
                {testResult.message}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
              disabled={loading || isTesting}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleTestConnection}
              disabled={loading || isTesting}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center gap-2 disabled:opacity-50"
            >
              {isTesting && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              Test
            </button>

            <button
              type="submit"
              disabled={loading || !testResult?.success}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
            >
              <PlusIcon className="w-4 h-4" />
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
